import {
  decodeTerrariumRgba,
  sampleTerrain,
  tileKeyString,
  tilesForRecords,
} from '@fit-editor/fit-core'
import type {
  ActivityRecord,
  DecodedTerrainTile,
  TerrainSource,
  TileKey,
} from '@fit-editor/fit-core'

/**
 * Fetch/decode orchestration for Mapterhorn terrain tiles, kept separate
 * from `fit-worker.ts` (and free of any Comlink import) so it can be unit
 * tested with an injected `fetch` and image decoder -- jsdom has neither
 * `createImageBitmap` nor `OffscreenCanvas`, and tests must never hit the
 * real network.
 *
 * Privacy: the only thing that leaves the browser is the tile URL
 * (`/{z}/{x}/{y}.webp`) -- never a coordinate list, a timestamp, or any
 * other part of the track. Requests are sent without credentials and
 * without a referrer.
 */

export const TERRAIN_TILE_URL_TEMPLATE = 'https://tiles.mapterhorn.com/{z}/{x}/{y}.webp'
export const TERRAIN_ATTRIBUTION = '© Mapterhorn (https://mapterhorn.com/attribution)'

/** High-resolution national DEMs (LiDAR etc.) -- only available at z13+ in some countries. */
export const HIGH_RES_ZOOM = 14
/** Copernicus GLO-30 base layer, available worldwide up to z12. */
export const BASE_ZOOM = 12
/** Sampling order passed to `sampleTerrain`: finest first, falling back per record. */
export const SAMPLE_ZOOMS = [HIGH_RES_ZOOM, BASE_ZOOM]
export const MAX_CONCURRENT_TILE_REQUESTS = 6

/**
 * Approximate ground resolution reported for each layer. z14 at 512 px/tile
 * samples at ~4.8 m/px at the equator (finer further north) from sources of
 * 0.25-5 m, so 5 m is an honest upper bound; z12 is backed by the 30 m
 * Copernicus GLO-30 DEM regardless of its ~19 m pixel size.
 */
export const HIGH_RES_RESOLUTION_M = 5
export const BASE_RESOLUTION_M = 30

export interface TerrainLoadProgress {
  done: number
  total: number
}

export interface TerrainLoadSummary {
  /** Distinct z14 (high-resolution) tiles the route touches that were available. */
  highResTiles: number
  /** z12 (30 m Copernicus) tiles used as a fallback where no z14 tile exists. */
  baseTiles: number
  /** Tiles unavailable at every zoom (e.g. open ocean) -- those records get no terrain value. */
  unavailableTiles: number
}

export interface TerrainLoadResult {
  source: TerrainSource
  summary: TerrainLoadSummary
  /** Human-readable description of the data actually used, for the UI caption. */
  description: string
}

export type FetchLike = (
  url: string,
  init: NonNullable<Parameters<typeof fetch>[1]>,
) => Promise<Response>
/** Decodes an image blob to tightly-packed RGBA pixels of a square tile. */
export type DecodeImage = (blob: Blob) => Promise<{ rgba: Uint8ClampedArray; size: number }>

export interface TerrainLoaderOptions {
  fetch?: FetchLike
  decodeImage?: DecodeImage
  concurrency?: number
  urlTemplate?: string
}

export interface TerrainLoadOptions {
  signal?: AbortSignal | undefined
  onProgress?: ((progress: TerrainLoadProgress) => void) | undefined
}

/** Thrown for any failure that aborts a load (a non-404 HTTP status, a network or decode error). */
export class TerrainLoadError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'TerrainLoadError'
  }
}

export function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error != null &&
    'name' in error &&
    (error as { name: unknown }).name === 'AbortError'
  )
}

function abortError(): Error {
  const error = new Error('Terrain loading was cancelled.')
  error.name = 'AbortError'
  return error
}

/** A function (not an inline check) so TypeScript doesn't narrow `signal.aborted` across awaits. */
function throwIfAborted(signal: AbortSignal | undefined): void {
  if (signal?.aborted === true) throw abortError()
}

/** Browser default: `createImageBitmap` + `OffscreenCanvas` (both available in workers). */
export const decodeImageWithOffscreenCanvas: DecodeImage = async (blob) => {
  const bitmap = await createImageBitmap(blob)
  try {
    const { width, height } = bitmap
    if (width !== height) {
      throw new TerrainLoadError(`Unexpected terrain tile shape ${width}x${height}.`)
    }
    const canvas = new OffscreenCanvas(width, height)
    const context = canvas.getContext('2d', { willReadFrequently: true })
    if (context == null) throw new TerrainLoadError('2D canvas is not available in this browser.')
    context.drawImage(bitmap, 0, 0)
    return { rgba: context.getImageData(0, 0, width, height).data, size: width }
  } finally {
    bitmap.close()
  }
}

type TileFetchOutcome = { status: 'ok'; tile: DecodedTerrainTile } | { status: 'missing' }

/** Runs `tasks` with at most `limit` in flight; rejects (and stops scheduling) on the first failure. */
async function runPool<T>(
  items: readonly T[],
  limit: number,
  task: (item: T) => Promise<void>,
  signal: AbortSignal | undefined,
): Promise<void> {
  let next = 0
  let failed = false
  const worker = async () => {
    while (!failed && next < items.length) {
      throwIfAborted(signal)
      const item = items[next++] as T
      try {
        await task(item)
      } catch (error) {
        failed = true
        throw error
      }
    }
  }
  const workers = Array.from({ length: Math.min(limit, items.length) }, () => worker())
  await Promise.all(workers)
}

/**
 * Session-lifetime terrain tile loader + decoded-tile cache. One instance
 * lives in the FIT worker; tiles are cached in memory only (the browser's
 * HTTP cache additionally keeps the raw WebP for Mapterhorn's 7-day TTL).
 */
export class TerrainLoader {
  private readonly tiles = new Map<string, DecodedTerrainTile>()
  /** Tiles that returned 404 ("not available at this zoom") -- never refetched this session. */
  private readonly missing = new Set<string>()
  private readonly fetchImpl: FetchLike
  private readonly decodeImage: DecodeImage
  private readonly concurrency: number
  private readonly urlTemplate: string

  constructor(options: TerrainLoaderOptions = {}) {
    this.fetchImpl = options.fetch ?? ((url, init) => fetch(url, init))
    this.decodeImage = options.decodeImage ?? decodeImageWithOffscreenCanvas
    this.concurrency = options.concurrency ?? MAX_CONCURRENT_TILE_REQUESTS
    this.urlTemplate = options.urlTemplate ?? TERRAIN_TILE_URL_TEMPLATE
  }

  getTile = (key: TileKey): DecodedTerrainTile | undefined => this.tiles.get(tileKeyString(key))

  /** Terrain elevation per record (aligned to `records`), from cached tiles only -- never fetches. */
  sample(records: ActivityRecord[]): (number | null)[] {
    return sampleTerrain(records, this.getTile, SAMPLE_ZOOMS)
  }

  /**
   * The number of z14 tiles touched by `records` whose terrain isn't
   * resolved by the cache yet (neither the z14 tile nor, if that one is
   * known to be missing, its z12 parent has been fetched). Non-zero after
   * e.g. a `setGpsConstant` edit moves points somewhere new -- the UI
   * offers "Load missing terrain tiles" rather than auto-fetching.
   */
  countUnresolvedTiles(records: ActivityRecord[]): number {
    let count = 0
    for (const key of tilesForRecords(records, HIGH_RES_ZOOM)) {
      if (!this.isResolved(key)) count++
    }
    return count
  }

  private isResolved(key: TileKey): boolean {
    const id = tileKeyString(key)
    if (this.tiles.has(id)) return true
    if (!this.missing.has(id)) return false
    const parentId = tileKeyString(parentTile(key, BASE_ZOOM))
    return this.tiles.has(parentId) || this.missing.has(parentId)
  }

  private async fetchTile(
    key: TileKey,
    signal: AbortSignal | undefined,
  ): Promise<TileFetchOutcome> {
    const url = this.urlTemplate
      .replace('{z}', String(key.z))
      .replace('{x}', String(key.x))
      .replace('{y}', String(key.y))
    let response: Response
    try {
      response = await this.fetchImpl(url, {
        signal: signal ?? null,
        mode: 'cors',
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
      })
    } catch (error) {
      if (isAbortError(error) || signal?.aborted === true) throw abortError()
      throw new TerrainLoadError(
        `Could not reach tiles.mapterhorn.com (${error instanceof Error ? error.message : String(error)}).`,
      )
    }
    if (response.status === 404) return { status: 'missing' }
    if (!response.ok) {
      throw new TerrainLoadError(
        `tiles.mapterhorn.com returned HTTP ${response.status} for tile ${tileKeyString(key)}.`,
      )
    }
    let decoded: { rgba: Uint8ClampedArray; size: number }
    try {
      decoded = await this.decodeImage(await response.blob())
    } catch (error) {
      if (isAbortError(error) || signal?.aborted === true) throw abortError()
      throw new TerrainLoadError(
        `Could not decode terrain tile ${tileKeyString(key)} (${error instanceof Error ? error.message : String(error)}).`,
      )
    }
    return {
      status: 'ok',
      tile: {
        key,
        size: decoded.size,
        elevations: decodeTerrariumRgba(decoded.rgba, decoded.size),
      },
    }
  }

  /**
   * Fetches every not-yet-cached tile `records` needs: all z14 tiles the
   * route touches, then the z12 parent of each z14 tile that 404'd. A 404
   * means "not available at this zoom" (expected outside countries with a
   * high-resolution national DEM); any other failure aborts the whole load
   * -- nothing from a failed load is committed to the cache, so there is
   * never a partially-covered overlay. Rejects with an `AbortError` when
   * `signal` is aborted.
   */
  async load(
    records: ActivityRecord[],
    options: TerrainLoadOptions = {},
  ): Promise<TerrainLoadResult> {
    const { signal, onProgress } = options
    throwIfAborted(signal)

    const staged = new Map<string, DecodedTerrainTile>()
    const stagedMissing = new Set<string>()
    const has = (id: string) => this.tiles.has(id) || staged.has(id)
    const isMissing = (id: string) => this.missing.has(id) || stagedMissing.has(id)

    const highResKeys = dedupe(tilesForRecords(records, HIGH_RES_ZOOM))
    const highResToFetch = highResKeys.filter((k) => {
      const id = tileKeyString(k)
      return !has(id) && !isMissing(id)
    })

    let done = 0
    let total = highResToFetch.length
    onProgress?.({ done, total })

    const fetchInto = async (key: TileKey) => {
      const outcome = await this.fetchTile(key, signal)
      const id = tileKeyString(key)
      if (outcome.status === 'ok') staged.set(id, outcome.tile)
      else stagedMissing.add(id)
      done++
      onProgress?.({ done, total })
    }

    await runPool(highResToFetch, this.concurrency, fetchInto, signal)

    const baseKeys = dedupe(
      highResKeys.filter((k) => isMissing(tileKeyString(k))).map((k) => parentTile(k, BASE_ZOOM)),
    )
    const baseToFetch = baseKeys.filter((k) => {
      const id = tileKeyString(k)
      return !has(id) && !isMissing(id)
    })
    total += baseToFetch.length
    onProgress?.({ done, total })
    await runPool(baseToFetch, this.concurrency, fetchInto, signal)

    throwIfAborted(signal)

    for (const [id, tile] of staged) this.tiles.set(id, tile)
    for (const id of stagedMissing) this.missing.add(id)

    const highResTiles = highResKeys.filter((k) => this.tiles.has(tileKeyString(k))).length
    const baseTiles = baseKeys.filter((k) => this.tiles.has(tileKeyString(k))).length
    const unavailableTiles = baseKeys.filter((k) => this.missing.has(tileKeyString(k))).length
    return describeResult({ highResTiles, baseTiles, unavailableTiles })
  }
}

function dedupe(keys: readonly TileKey[]): TileKey[] {
  const seen = new Map<string, TileKey>()
  for (const key of keys) seen.set(tileKeyString(key), key)
  return [...seen.values()]
}

/** The ancestor of `key` at zoom `z` (`z <= key.z`). */
export function parentTile(key: TileKey, z: number): TileKey {
  const shift = key.z - z
  return { z, x: key.x >> shift, y: key.y >> shift }
}

function describeResult(summary: TerrainLoadSummary): TerrainLoadResult {
  const { highResTiles, baseTiles } = summary
  const highResLabel = 'high-resolution national DEM (≤5 m)'
  const baseLabel = 'Copernicus GLO-30 (30 m)'
  let description: string
  if (highResTiles > 0 && baseTiles > 0) {
    description = `Mixed: ${highResLabel} for ${highResTiles} tile${highResTiles === 1 ? '' : 's'}, ${baseLabel} for ${baseTiles} tile${baseTiles === 1 ? '' : 's'}`
  } else if (highResTiles > 0) {
    description = highResLabel
  } else if (baseTiles > 0) {
    description = baseLabel
  } else {
    description = 'No terrain data available for this route'
  }
  return {
    source: {
      name: 'Mapterhorn',
      resolutionM: highResTiles > 0 ? HIGH_RES_RESOLUTION_M : BASE_RESOLUTION_M,
      attribution: TERRAIN_ATTRIBUTION,
    },
    summary,
    description,
  }
}
